import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map } from 'rxjs';
import { AppConfigService } from './app-config.service';
import { INDEXED_DB_KEYS, IndexedDbService } from './indexed-db.service';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private appConfig = inject(AppConfigService);
  private indexedDbService = inject(IndexedDbService);
  private prefix = this.appConfig.dbName + '_';

  selectedFiltersSubject$ = new BehaviorSubject(this.getStoredFilters());
  selectedFirmIdsSubject$ = new BehaviorSubject<string[] | null>(null);
  filterCountResultSubject$ = new BehaviorSubject(JSON.parse(this.getItem('filterCountResult')));
  selectedVariablesSubject$ = new BehaviorSubject(JSON.parse(this.getItem('variables'))?.variables);
  storedYearsSubject$ = new BehaviorSubject(JSON.parse(this.getItem('years'))?.years ?? []);
  selectedExtraOptionsSubject$ = new BehaviorSubject(JSON.parse(this.getItem('extra-options')));
  selectedDownloadOptionsSubject$ = new BehaviorSubject(JSON.parse(this.getItem('download-options')));
  selectedFileNameSubject$ = new BehaviorSubject(this.getItem('download-filename') || 'extract');
  dbVersionSubject$ = new BehaviorSubject(JSON.parse(this.getItem('dbVersion')) || 'latest');
  excludeMetaOnlyFirmsSubject$ = new BehaviorSubject(this.getStoredExcludeMetaOnlyFirms());

  private getStoredFilters() {
    const stored = JSON.parse(this.getItem('filter'));
    if (stored?.filters) return stored.filters;
    if (Array.isArray(stored)) return stored;
    return null;
  }

  private getStoredExcludeMetaOnlyFirms() {
    const stored = JSON.parse(this.getItem('filter'));
    if (stored?.excludeMetaOnlyFirms !== undefined) return stored.excludeMetaOnlyFirms;
    return true;
  }

  // Catch ['undefined'] as selected variables in local storage
  constructor() {
    const selectedVariablesSnapShot = this.selectedVariablesSubject$.getValue();
    if (selectedVariablesSnapShot && selectedVariablesSnapShot.length > 0 && selectedVariablesSnapShot[0] == 'undefined') {
      this.setItem('variables', JSON.stringify({ variables: [] }));
      this.selectedVariablesSubject$.next([]);
    }

    void this.hydrateSelectedFirmIds();
  }

  private async hydrateSelectedFirmIds(): Promise<void> {
    const firmIdList = await this.indexedDbService.getItem<string[]>(INDEXED_DB_KEYS.firmIdList);
    this.selectedFirmIdsSubject$.next(firmIdList ?? null);
  }


  selectionCompleted$ = combineLatest([
    this.selectedFiltersSubject$,
    this.selectedFirmIdsSubject$,
    this.selectedVariablesSubject$,
    this.storedYearsSubject$,
    this.selectedExtraOptionsSubject$]).pipe(
    map(([filter, firmIdList, variables, years, extraOptions]) => {
      const firms = !!filter || !!firmIdList;
      const requiredSteps = [firms, variables, years];
      if (requiredSteps.every(step => !!step)) {
        return 'every';
      } else if ([...requiredSteps, extraOptions].some(step => !!step)) {
        return 'some';
      } else {
        return 'none';
      }
    })
  );

  getItem(name: string) {
    return localStorage.getItem(this.prefix + name);
  }

  setItem(name: string, value: string) {
    return localStorage.setItem(this.prefix + name, value);
  }

  setToken(value: string) {
    this.setItem('sequenza_token', value);
    this.setItem('sequenza_token_acquired_at', JSON.stringify(Date.now()));
  }

  removeItem(name: string) {
    return localStorage.removeItem(this.prefix + name);
  }

  parseQuery() {
    const filters = this.selectedFiltersSubject$.getValue() ?? [];
    const excludeMetaOnlyFirms = this.excludeMetaOnlyFirmsSubject$.getValue();
    const variables = this.selectedVariablesSubject$.getValue() ?? [];
    const years = this.storedYearsSubject$.getValue() ?? [];
    const extraOptions = this.selectedExtraOptionsSubject$.getValue();
    const downloadOptions = this.selectedDownloadOptionsSubject$.getValue();
    const firmIdList = this.selectedFirmIdsSubject$.getValue() ?? [];
    const filename = this.selectedFileNameSubject$.getValue();
    const dbVersion = this.dbVersionSubject$.getValue() || 'latest';
    return {filters, excludeMetaOnlyFirms, variables, years, extraOptions, downloadOptions, firmIdList, dbVersion, filename};
  }
}
